import React, { useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import {
  UserIcon,
  LockClosedIcon,
  ArrowRightOnRectangleIcon,
  SparklesIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline'
import { AlertModal } from '@/components/ConfirmModal'
import Register from './Register'

export const Login: React.FC = () => {
  const { login, loginWithTelegram, isLoading } = useAuth()
  
  const [isRegisterMode, setIsRegisterMode] = useState(false)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  
  const [alertDialog, setAlertDialog] = useState<{
    isOpen: boolean
    title?: string
    message: string
    type?: 'success' | 'error' | 'info'
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  })

  // Check if inside Telegram WebApp
  const isInsideTelegram = typeof window !== 'undefined' && !!(window as any).Telegram?.WebApp?.initDataUnsafe?.user

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!username.trim() || !password) {
      setAlertDialog({
        isOpen: true,
        type: 'error',
        title: 'خطا',
        message: 'لطفاً نام کاربری و رمز عبور را وارد کنید.'
      })
      return
    }

    try {
      await login(username.trim(), password)
    } catch (err: any) {
      setAlertDialog({
        isOpen: true,
        type: 'error',
        title: 'خطا در ورود',
        message: err.response?.data?.detail || 'نام کاربری یا رمز عبور اشتباه است.'
      })
    }
  }

  const handleTelegramAuth = async () => {
    try {
      await loginWithTelegram()
    } catch (err: any) {
      setAlertDialog({
        isOpen: true,
        type: 'error',
        title: 'خطا',
        message: err.response?.data?.detail || 'حساب کاربری متصل به تلگرام یافت نشد.'
      })
    }
  }

  if (isRegisterMode) {
    return <Register onSwitchToLogin={() => setIsRegisterMode(false)} />
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 p-4">
      <div className="w-full max-w-md space-y-6 animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-3xl bg-emerald-600 text-white shadow-xl shadow-emerald-500/20 mb-2">
            <SparklesIcon className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-extrabold text-slate-800 dark:text-white">
            ورود به سیستم حضور و غیاب
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            برای مشاهده کارکرد و ثبت تردد وارد شوید
          </p>
        </div>

        {/* Login Form Card */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-xl border border-slate-100 dark:border-slate-700/60 space-y-4">
          <form onSubmit={handleLogin} className="space-y-4">
            {/* Username */}
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                نام کاربری:
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="نام کاربری..."
                  dir="ltr"
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl pr-10 pl-3 py-2.5 text-xs font-semibold text-slate-800 dark:text-white outline-none focus:border-emerald-500 text-left"
                  required
                />
                <UserIcon className="w-4 h-4 text-slate-400 absolute right-3.5 top-3" />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                رمز عبور:
              </label>
              <div className="relative">
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  dir="ltr"
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl pr-10 pl-3 py-2.5 text-xs font-semibold text-slate-800 dark:text-white outline-none focus:border-emerald-500 text-left"
                  required
                />
                <LockClosedIcon className="w-4 h-4 text-slate-400 absolute right-3.5 top-3" />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl font-bold text-xs shadow-lg shadow-emerald-600/20 transition active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              {isLoading ? 'در حال ورود...' : 'ورود به حساب'}
            </button>
          </form>

          {/* Telegram One-Click Login (if in web app or linked) */}
          {isInsideTelegram && (
            <div className="pt-2">
              <button
                type="button"
                onClick={handleTelegramAuth}
                disabled={isLoading}
                className="w-full py-2.5 bg-blue-50 dark:bg-blue-950/40 hover:bg-blue-100 text-blue-600 dark:text-blue-300 border border-blue-200 dark:border-blue-800/50 rounded-2xl font-bold text-xs transition flex items-center justify-center gap-2"
              >
                <PaperAirplaneIcon className="w-4 h-4" />
                ورود سریع با تلگرام
              </button>
            </div>
          )}

          {/* Switch to Register */}
          <div className="pt-3 border-t border-slate-100 dark:border-slate-700 text-center">
            <button
              type="button"
              onClick={() => setIsRegisterMode(true)}
              className="text-xs text-emerald-600 dark:text-emerald-400 font-bold hover:underline"
            >
              کد دعوت دارید؟ ثبت‌نام حساب جدید
            </button>
          </div>
        </div>
      </div>

      <AlertModal
        isOpen={alertDialog.isOpen}
        title={alertDialog.title}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  )
}

export default Login
